/**
 * AIM200 overlay renderer — browser copy.
 *
 * Keep in sync with backend/src/services/hazardOverlay/renderer.ts.
 * This file exists separately because CRA does not support shared workspace packages.
 *
 * Mirrors draw_overlay() in aim200-metadata-verify-module/main.cpp:193-235.
 */

import type {OverlayFrame, OverlayMask} from '../../../../interfaces/HazardAILog';

export interface RenderOpts {
  showBoxes?: boolean;
  showMasks?: boolean;
  /** 0.5 = translucent (default), 1.0 = opaque (production look) */
  maskAlpha?: number;
  /**
   * Pixel offset from the canvas origin to the top-left corner of the rendered
   * video content (non-zero when object-fit: contain produces letterbox bars).
   * Defaults to 0 — backend usage (native-resolution frames, no bars) omits these.
   */
  offsetX?: number;
  offsetY?: number;
}

const decodeRleMask = (mask: OverlayMask): Float32Array => {
  // Browser base64 decode via atob
  const binary = atob(mask.rle);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  const floats = new Float32Array(mask.w * mask.h);
  let fi = 0;
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    // bit7 = pixel value; bits[6:0]+1 = run length — no bitwise operators per linting rules
    const value = b >= 128 ? 1.0 : 0.0;
    const run = (b % 128) + 1;
    for (let r = 0; r < run; r++) {
      if (fi < floats.length) {
        floats[fi++] = value;
      }
    }
  }
  return floats;
};

/**
 * Render one frame's detections onto a Canvas 2D context.
 *
 * @param ctx    Canvas 2D rendering context
 * @param frame  Sidecar frame for the current presentation time
 * @param scaleX canvas.width / sourceWidth
 * @param scaleY canvas.height / sourceHeight
 * @param opts   Rendering options
 */
export const renderOverlay = (
  ctx: CanvasRenderingContext2D,
  frame: OverlayFrame,
  scaleX: number,
  scaleY: number,
  opts: RenderOpts = {}
): void => {
  const showBoxes = opts.showBoxes !== false;
  const showMasks = opts.showMasks !== false;
  const maskAlpha = opts.maskAlpha ?? 0.5;
  const offsetX = opts.offsetX ?? 0;
  const offsetY = opts.offsetY ?? 0;

  for (const obj of frame.objects) {
    const [srcL, srcT, srcW, srcH] = obj.bbox;
    const L = srcL * scaleX + offsetX;
    const T = srcT * scaleY + offsetY;
    const W = srcW * scaleX;
    const H = srcH * scaleY;

    // 1. Instance mask — orange alpha blend (main.cpp:201-215)
    if (showMasks && obj.mask) {
      const floats = decodeRleMask(obj.mask);
      const {w: mw, h: mh} = obj.mask;
      const bboxW = Math.max(1, Math.round(W));
      const bboxH = Math.max(1, Math.round(H));
      const imgData = ctx.createImageData(bboxW, bboxH);
      const px = imgData.data;

      for (let py = 0; py < bboxH; py++) {
        const srcY = Math.min(mh - 1, Math.floor((py / bboxH) * mh));
        for (let px2 = 0; px2 < bboxW; px2++) {
          const srcX = Math.min(mw - 1, Math.floor((px2 / bboxW) * mw));
          const maskVal = floats[srcY * mw + srcX];
          const idx = (py * bboxW + px2) * 4;
          if (maskVal > 0.5) {
            px[idx]     = 255; // R
            px[idx + 1] = 128; // G  → rgb(255,128,0) orange (main.cpp:190)
            px[idx + 2] = 0;   // B
            px[idx + 3] = Math.round(maskAlpha * 255);
          }
        }
      }

      ctx.putImageData(imgData, Math.max(0, Math.round(L)), Math.max(0, Math.round(T)));
    }

    // 2. Bounding box — magenta, thickness 2 (main.cpp:218-219)
    if (showBoxes) {
      ctx.save();
      ctx.strokeStyle = 'rgb(255,0,255)';
      ctx.lineWidth = 2;
      ctx.strokeRect(L, T, W, H);
      ctx.restore();
    }

    // 3. Label — "<zone> zone <depth> m", white on black, +50,+50 from bbox TL (main.cpp:222-233)
    if (showBoxes) {
      const zoneName =
        obj.zone === 'haz'  ? 'Haz'
        :  obj.zone === 'warn' ? 'Warn'
        : obj.zone === 'id'   ? 'ID'   : 'None';
      const label = `${zoneName} zone ${obj.depthM.toFixed(2)} m`;
      const lx = L + 50 * scaleX;
      const ly = T + 50 * scaleY;
      const fontSize = Math.round(13 * Math.min(scaleX, scaleY));
      ctx.save();
      ctx.font = `${fontSize}px sans-serif`;
      const tm = ctx.measureText(label);
      const textH = fontSize;
      ctx.fillStyle = 'black';
      ctx.fillRect(lx, ly - textH, tm.width + 4, textH + 4);
      ctx.fillStyle = 'white';
      ctx.fillText(label, lx + 2, ly);
      ctx.restore();
    }
  }
};
